import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
    FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaFacebookF, FaLinkedinIn, FaInstagram, FaYoutube,
    FaGlobe, FaChevronDown, FaWhatsapp, FaBars, FaTimes,
} from 'react-icons/fa';
import { useLanguage } from '../../i18n';
import { PRACTICE_AREAS } from '../../data/practiceAreas';
import { SOCIAL_PROFILES } from '../../seo/config';
import logo from '../../images/logo/logo.png';
import './style.scss';

const PHONE = '+919555690946';
const PHONE_LABEL = '+91-9555 690 946';
const EMAIL = 'contact@legalcorridors.com';
const WHATSAPP = `https://wa.me/${PHONE.slice(1)}?text=${encodeURIComponent('Hello, I would like to schedule a consultation.')}`;
const SOCIAL = [
    { Icon: FaFacebookF, label: 'Facebook', href: SOCIAL_PROFILES[0] },
    { Icon: FaInstagram, label: 'Instagram', href: SOCIAL_PROFILES[1] },
    { Icon: FaLinkedinIn, label: 'LinkedIn', href: SOCIAL_PROFILES[2] },
    { Icon: FaYoutube, label: 'YouTube', href: SOCIAL_PROFILES[3] },
];

// Site-wide header: slim utility bar + sticky main bar on desktop; a compact
// sticky bar with a slide-in menu on mobile.
const SiteHeader = () => {
    const { t, language, toggleLanguage } = useLanguage();
    const { pathname } = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [practiceOpen, setPracticeOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const menuButtonRef = useRef(null);

    const nav = [
        { to: '/', label: t('nav.home'), end: true },
        { to: '/about', label: t('nav.about') },
        { to: '/team', label: t('nav.team') },
        { to: '/practice', label: t('nav.practice'), children: true },
        { to: '/blogs', label: language === 'hi' ? 'कानूनी लेख' : 'Legal Insights' },
        { to: '/contact', label: t('nav.contact') },
    ];

    // Close menus on navigation.
    useEffect(() => {
        setMenuOpen(false);
        setPracticeOpen(false);
    }, [pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Mobile menu: lock page scroll and close on Escape.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const menuButton = menuButtonRef.current;
        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('is-menu-open');
        const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = overflow;
            document.body.classList.remove('is-menu-open');
            window.removeEventListener('keydown', onKey);
            if (menuButton) menuButton.focus(); // return focus to the menu button
        };
    }, [menuOpen]);

    const isBlogSection = pathname.startsWith('/blog');

    return (
        <header className="siteHeader">
            <div className="siteHeader__utility">
                <div className="container siteHeader__utilityInner">
                    <ul className="siteHeader__contact">
                        <li><FaMapMarkerAlt aria-hidden="true" /> {t('header.locations')}</li>
                        <li><a href={`tel:${PHONE}`}><FaPhoneAlt aria-hidden="true" /> {PHONE_LABEL}</a></li>
                        <li><a href={`mailto:${EMAIL}`}><FaEnvelope aria-hidden="true" /> {EMAIL}</a></li>
                    </ul>
                    <div className="siteHeader__utilityRight">
                        <button type="button" className="siteHeader__lang" onClick={toggleLanguage}>
                            <FaGlobe aria-hidden="true" /> {language === 'en' ? 'हिंदी' : 'English'}
                        </button>
                        <ul className="siteHeader__social">
                            {SOCIAL.map(({ Icon, label, href }) => (
                                <li key={label}>
                                    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}><Icon /></a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <div className={`siteHeader__main${scrolled ? ' is-scrolled' : ''}`}>
                <div className="container siteHeader__mainInner">
                    <Link to="/" className="siteHeader__logo" aria-label="Legal Corridor — home">
                        <img src={logo} alt="Legal Corridor" width="1200" height="360" />
                    </Link>

                    <nav className="siteHeader__nav" aria-label="Main">
                        <ul>
                            {nav.map((item) => (
                                <li key={item.to} className={item.children ? 'has-dropdown' : undefined}>
                                    <NavLink
                                        to={item.to}
                                        end={item.end}
                                        className={({ isActive }) => (isActive || (item.to === '/blogs' && isBlogSection) ? 'is-active' : undefined)}
                                    >
                                        {item.label}
                                        {item.children && <FaChevronDown className="siteHeader__chevron" aria-hidden="true" />}
                                    </NavLink>
                                    {item.children && (
                                        <div className="siteHeader__dropdown">
                                            <ul>
                                                {PRACTICE_AREAS.map((p) => (
                                                    <li key={p.slug}><NavLink to={`/practice/${p.slug}`}>{p.name}</NavLink></li>
                                                ))}
                                            </ul>
                                            <Link to="/practice" className="siteHeader__dropdownAll">
                                                {language === 'hi' ? 'सभी प्रैक्टिस क्षेत्र' : 'All practice areas'} →
                                            </Link>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className="siteHeader__actions">
                        <a href={`tel:${PHONE}`} className="siteHeader__call" aria-label={`Call ${PHONE_LABEL}`}>
                            <FaPhoneAlt aria-hidden="true" />
                        </a>
                        <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="siteHeader__cta">
                            {t('header.freeConsultation')}
                        </a>
                        <button
                            ref={menuButtonRef}
                            type="button"
                            className="siteHeader__menuBtn"
                            aria-label="Open menu"
                            aria-expanded={menuOpen}
                            aria-controls="mobile-menu"
                            onClick={() => setMenuOpen(true)}
                        >
                            <FaBars />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            <div className={`siteHeader__overlay${menuOpen ? ' is-open' : ''}`} onClick={() => setMenuOpen(false)} aria-hidden="true" />
            <div
                id="mobile-menu"
                className={`siteHeader__drawer${menuOpen ? ' is-open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Menu"
                aria-hidden={!menuOpen}
            >
                <div className="siteHeader__drawerHead">
                    <img src={logo} alt="Legal Corridor" width="1200" height="360" />
                    <button type="button" className="siteHeader__close" aria-label="Close menu" onClick={() => setMenuOpen(false)} tabIndex={menuOpen ? 0 : -1}>
                        <FaTimes />
                    </button>
                </div>
                <nav aria-label="Mobile">
                    <ul className="siteHeader__drawerNav">
                        {nav.map((item) => (
                            <li key={item.to}>
                                {item.children ? (
                                    <>
                                        <button
                                            type="button"
                                            className={`siteHeader__drawerLink${practiceOpen ? ' is-expanded' : ''}`}
                                            aria-expanded={practiceOpen}
                                            onClick={() => setPracticeOpen((v) => !v)}
                                            tabIndex={menuOpen ? 0 : -1}
                                        >
                                            {item.label}
                                            <FaChevronDown aria-hidden="true" />
                                        </button>
                                        <ul className={`siteHeader__drawerSub${practiceOpen ? ' is-open' : ''}`}>
                                            <li><Link to="/practice" tabIndex={menuOpen && practiceOpen ? 0 : -1}>{language === 'hi' ? 'सभी प्रैक्टिस क्षेत्र' : 'All practice areas'}</Link></li>
                                            {PRACTICE_AREAS.map((p) => (
                                                <li key={p.slug}>
                                                    <Link to={`/practice/${p.slug}`} tabIndex={menuOpen && practiceOpen ? 0 : -1}>{p.name}</Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <NavLink
                                        to={item.to}
                                        end={item.end}
                                        className={({ isActive }) => `siteHeader__drawerLink${isActive || (item.to === '/blogs' && isBlogSection) ? ' is-active' : ''}`}
                                        tabIndex={menuOpen ? 0 : -1}
                                    >
                                        {item.label}
                                    </NavLink>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="siteHeader__drawerFoot">
                    <a href={`tel:${PHONE}`} className="siteHeader__drawerBtn is-primary" tabIndex={menuOpen ? 0 : -1}>
                        <FaPhoneAlt aria-hidden="true" /> {language === 'hi' ? 'अभी कॉल करें' : 'Call now'}
                    </a>
                    <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="siteHeader__drawerBtn is-whatsapp" tabIndex={menuOpen ? 0 : -1}>
                        <FaWhatsapp aria-hidden="true" /> WhatsApp
                    </a>
                    <button type="button" className="siteHeader__drawerLang" onClick={toggleLanguage} tabIndex={menuOpen ? 0 : -1}>
                        <FaGlobe aria-hidden="true" /> {language === 'en' ? 'हिंदी में पढ़ें' : 'Read in English'}
                    </button>
                    <ul className="siteHeader__social is-drawer">
                        {SOCIAL.map(({ Icon, label, href }) => (
                            <li key={label}>
                                <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} tabIndex={menuOpen ? 0 : -1}><Icon /></a>
                            </li>
                        ))}
                    </ul>
                    <p className="siteHeader__drawerMeta">{t('header.locations')}</p>
                </div>
            </div>
        </header>
    );
};

export default SiteHeader;
